import React, { useCallback, useEffect, useMemo, useState } from "react";
import useEntriesService from "../../../services/useEntriesService";
import { IrrelevantEntry } from "../../../types/Entries";
import SurfaceCard from "../../../components/Common/SurfaceCard";
import { Box, Button, IconButton, InputAdornment, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip } from "@mui/material";
import LicensePlate from "../../../components/LicensePlate";
import SearchIcon from '@mui/icons-material/Search';
import NotificationImportantOutlinedIcon from '@mui/icons-material/NotificationImportantOutlined';
import BlockIcon from '@mui/icons-material/Block';
import { useTranslation } from "react-i18next";
import useToast, { ToastType } from "../../../hooks/useToast";
import useCanAccess from "../../../hooks/useCanAccess";
import MarkIrrelevantDialog from "../../../components/TableView/MarkIrrelevantDialog";

function IrrelevantEntries() {
    const [irrelevantLicensePlates, setIrrelevantLicensePlates] = useState<IrrelevantEntry[]>([]);
    const [searchText, setSearchText] = useState('');
    const [markDialogOpen, setMarkDialogOpen] = useState(false);
    const [isMarkingIrrelevant, setIsMarkingIrrelevant] = useState(false);

    const { getIrrelevantLicensePlates, markCarAsIrrelevant } = useEntriesService();
    const { t } = useTranslation("common");
    const { showToast } = useToast();
    const { canAccessAction } = useCanAccess();

    useEffect(() => {
        getIrrelevantLicensePlates().then(plates => {
            setIrrelevantLicensePlates(plates);
        });
    }, [getIrrelevantLicensePlates]);

    const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(event.target.value);
    }, []);

    const handleMarkDialogCancel = useCallback(() => {
        setMarkDialogOpen(false);
    }, []);

    const handleMarkAsIrrelevantConfirm = useCallback(async (reason: string, plate?: string) => {
        if (!plate) return;
        setIsMarkingIrrelevant(true);
        try {
            await markCarAsIrrelevant(plate, true, reason);
            showToast({
                type: ToastType.SUCCESS,
                message: t('search.markAsIrrelevantSuccess.irrelevant', { licensePlate: plate }),
            });
            setMarkDialogOpen(false);
            getIrrelevantLicensePlates().then(plates => setIrrelevantLicensePlates(plates));
        } catch (error) {
            showToast({
                type: ToastType.ERROR,
                message: t('common.errorOccurred'),
            });
        } finally {
            setIsMarkingIrrelevant(false);
        }
    }, [markCarAsIrrelevant, showToast, t, getIrrelevantLicensePlates]);

    const handleMarkAsRelevant = useCallback(async (licenseplate: string) => {
        showToast({
            type: ToastType.CONFIRM,
            message: '',
            confirmMessage: t('administration.irrelevantEntries.confirmMarkAsRelevant', { licensePlate: licenseplate }),
            confirmCallback: async () => {
                try {
                    await markCarAsIrrelevant(licenseplate, false);
                    showToast({
                        type: ToastType.SUCCESS,
                        message: t('administration.irrelevantEntries.licensePlateMarkedAsRelevant', { licensePlate: licenseplate }),
                    });
                    setIrrelevantLicensePlates(prev =>
                        prev.filter(entry => entry.licenseplate !== licenseplate)
                    );
                } catch (error) {
                    showToast({
                        type: ToastType.ERROR,
                        message: t('common.errorOccurred'),
                    });
                }
            },
        });
    }, [markCarAsIrrelevant, showToast, t]);

    const filteredEntries = useMemo(() => {
        if (!searchText.trim()) return irrelevantLicensePlates;
        const lowerSearch = searchText.toLowerCase();
        return irrelevantLicensePlates.filter(entry =>
            entry.licenseplate?.toLowerCase().includes(lowerSearch));
    }, [irrelevantLicensePlates, searchText]);

    const searchField = useMemo(() => (
        <TextField
            variant="outlined"
            placeholder={t("search.filters.searchByLicensePlate")}
            value={searchText}
            onChange={handleSearchChange}
            InputProps={{
                endAdornment: (
                    <InputAdornment position="end">
                        <SearchIcon />
                    </InputAdornment>
                ),
                inputProps: { sx: { pt: '8.5px', pb: '8.5px' } },
            }}
            style={{ width: '180px', borderRadius: '4px' }}
        />
    ), [t, searchText, handleSearchChange]);

    return (
        <>
        <MarkIrrelevantDialog
            open={markDialogOpen}
            licensePlate={null}
            onConfirm={handleMarkAsIrrelevantConfirm}
            onCancel={handleMarkDialogCancel}
            isLoading={isMarkingIrrelevant}
        />
        <SurfaceCard>
            <TableContainer sx={{ maxHeight: '100%' }}>
                <Table stickyHeader sx={{ tableLayout: { xs: 'fixed', sm: 'auto' } }}>
                    <TableHead>
                        <TableRow>
                            <TableCell colSpan={5} sx={{ p: 1 }}>
                                <Box
                                    alignItems="center"
                                    display="flex"
                                    gap={2}
                                    justifyContent="space-between"
                                    width="100%"
                                >
                                    {searchField}
                                    {canAccessAction('markIrrelevant') && (
                                        <Button
                                            variant="outlined"
                                            startIcon={<BlockIcon />}
                                            onClick={() => setMarkDialogOpen(true)}
                                        >
                                            {t('search.markAsIrrelevant')}
                                        </Button>
                                    )}
                                </Box>
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>{t('administration.irrelevantEntries.licensePlate')}</TableCell>
                            <TableCell>{t('administration.irrelevantEntries.dateAndTime')}</TableCell>
                            <TableCell>{t('administration.irrelevantEntries.reason')}</TableCell>
                            <TableCell>{t('administration.irrelevantEntries.createdBy')}</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredEntries.map((entry, index) => (
                            <TableRow
                                hover
                                key={entry.licenseplate}
                                sx={{
                                    backgroundColor: index % 2 === 0 ? 'rgba(255, 255, 255, 0.05)' : 'inherit',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.1) !important',
                                    },
                                }}
                            >
                                <TableCell>
                                    <LicensePlate
                                        licenseNumber={entry.licenseplate}
                                    />
                                </TableCell>
                                <TableCell>
                                    {new Date(entry.createdat).toLocaleString()}
                                </TableCell>
                                <TableCell>
                                    {entry.reason}
                                </TableCell>
                                <TableCell>
                                    {entry.createdbystaffname}
                                </TableCell>
                                <TableCell align="right">
                                    {canAccessAction('markIrrelevant') && (
                                        <>
                                            <Tooltip title={t('administration.irrelevantEntries.markAsRelevant')}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleMarkAsRelevant(entry.licenseplate)}
                                                >
                                                    <NotificationImportantOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </SurfaceCard>
        </>
    );
}

export default IrrelevantEntries;
